import { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

import { useMemo } from 'react';
import { useStripeData } from "@/hooks/useStripeData";

export default function RevenueChart() {
  const { t } = useTranslation();
  const [range, setRange] = useState<number>(30); // Default to 30 days
  const { transactions, isLoading } = useStripeData();

  const timeRanges = [
    { label: t('admin.finance.chart.ranges.7d'), value: 7 },
    { label: t('admin.finance.chart.ranges.30d'), value: 30 },
    { label: t('admin.finance.chart.ranges.90d'), value: 90 },
    { label: t('admin.finance.chart.ranges.1y'), value: 365 },
    { label: t('admin.finance.chart.ranges.2y'), value: 730 },
    { label: t('admin.finance.chart.ranges.3y'), value: 1095 },
  ];

  const chartData = useMemo(() => {
    if (!transactions) return [];
    
    // Group transactions by date
    const grouped = transactions.reduce((acc, tx) => {
        // Use full date for sorting then format for display if needed, 
        // but sticking to existing logic for now which seems to use formatted date as key.
        // Better to use ISO date as key for sorting correctness.
        const date = new Date(tx.created_at);
        const key = date.toISOString().split('T')[0]; // YYYY-MM-DD
        acc[key] = (acc[key] || 0) + (tx.amount / 100);
        return acc;
    }, {} as Record<string, number>);

    // Convert to array, sort by date, and slice based on range
    const sortedData = Object.entries(grouped)
        .map(([date, revenue]) => ({ 
            date, // keep original date for sorting
            name: new Date(date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' }), // display name
            revenue 
        }))
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        
    // Slice based on range
    return sortedData.slice(-range); 
  }, [transactions, range]);

  return (
    <div className="rounded-2xl border-2 border-black bg-white p-8 shadow-[4px_4px_0_#000] mb-8">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-xl font-black">{t('admin.finance.chart.title')}</h3>
          <p className="text-sm font-bold text-gray-500">{t('admin.finance.chart.subtitle')}</p>
        </div>
        <div className="flex flex-wrap gap-2 rounded-xl border-2 border-black bg-gray-100 p-1">
          {timeRanges.map((r) => (
            <button
              key={r.value}
              onClick={() => setRange(r.value)}
              className={cn(
                "rounded-lg px-3 py-1.5 text-xs font-bold transition-all sm:text-sm whitespace-nowrap",
                range === r.value 
                  ? "bg-white text-black shadow-sm ring-2 ring-black" 
                  : "text-gray-500 hover:text-black"
              )}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="h-[300px] w-full">
        {isLoading ? (
            <div className="h-full w-full flex items-center justify-center font-bold text-gray-400">
                {t('admin.finance.chart.loading')}
            </div>
        ) : (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData.length > 0 ? chartData : [{ name: t('admin.finance.chart.noData'), revenue: 0 }]}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
            <XAxis 
              dataKey="name" 
              axisLine={false} 
              tickLine={false} 
              tick={{ fill: '#6B7280', fontSize: 12, fontWeight: 700 }}
              dy={10}
            />
            <YAxis 
              axisLine={false} 
              tickLine={false} 
              tick={{ fill: '#6B7280', fontSize: 12, fontWeight: 700 }}
              tickFormatter={(value) => `$${value}`}
            />
            <Tooltip 
              cursor={{ fill: '#F3F4F6' }}
              contentStyle={{ 
                borderRadius: '12px', 
                border: '2px solid black', 
                boxShadow: '4px 4px 0 #000',
                fontWeight: 'bold'
              }}
            />
            <Bar 
              dataKey="revenue" 
              fill="#FF6B9D" 
              radius={[4, 4, 0, 0]} 
              barSize={40}
            />
          </BarChart>
        </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
